import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "sc_access_token";

// Auth model:
//   • Primary: a first-party httpOnly cookie (`access_token`) set by the
//     server on /auth/login, /auth/register, /auth/google/session,
//     /auth/linkedin/callback. The cookie is `Secure; HttpOnly; SameSite=None`
//     so it survives strict ITP on the deployed domain (first-party).
//   • Back-compat: any stale Bearer token in localStorage from a previous
//     release is still attached as an Authorization header so existing
//     sessions keep working until they expire (7 days).
export const api = axios.create({
  baseURL: `${BASE}/api`,
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  // Legacy back-compat: if a localStorage token was set by an older release,
  // attach it as Authorization so existing sessions don't 401 until they expire.
  // NEW logins never write to localStorage (see setToken below) — the httpOnly
  // `access_token` cookie set by the server is the authoritative auth carrier.
  try {
    const t = typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) : null;
    if (t && !cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  } catch { /* incognito / disabled storage */ }

  // SEC-002: double-submit CSRF defense. On state-changing methods we
  // read the non-httpOnly `csrf_token` cookie set by the server on login
  // and echo it back as `X-CSRF-Token`. Cross-site attackers cannot read
  // this cookie (same-origin policy on cookies) so they cannot forge the
  // matching header, even if the k8s ingress rewrites the Origin header.
  //
  // NOTE: axios v1's `cfg.headers` is an ``AxiosHeaders`` instance; using
  // ``.set()`` is the documented API and works reliably even when the
  // caller supplied a per-request headers object (e.g. FormData uploads
  // with an explicit Content-Type). Bracket-notation writes were
  // observed to be swallowed on some builds, causing 403 CSRF failures
  // on the bulk-CSV candidate import endpoint.
  try {
    const method = String(cfg.method || "get").toLowerCase();
    if (!["get", "head", "options"].includes(method) && typeof document !== "undefined") {
      const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
      if (m) {
        const token = decodeURIComponent(m[1]);
        if (!cfg.headers) cfg.headers = {};
        if (typeof cfg.headers.set === "function") {
          if (!cfg.headers.get?.("X-CSRF-Token")) cfg.headers.set("X-CSRF-Token", token);
        } else if (!cfg.headers["X-CSRF-Token"]) {
          cfg.headers["X-CSRF-Token"] = token;
        }
      }
    }
  } catch { /* ignore */ }
  return cfg;
});

/** No-op kept for source compatibility — the server now sets an
 *  httpOnly cookie itself; we deliberately do NOT store the token
 *  in localStorage anymore. */
export function setToken(_token) {
  /* intentionally empty — cookie is authoritative */
}
export function clearToken() {
  try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}
/** Returns whatever legacy token is still in localStorage, if any.
 *  Used only by AuthContext to detect "I might be authed, ping /auth/me".
 *  Returns `true` whenever cookies are enabled so cookie-only users are
 *  still checked. */
export function getToken() {
  try {
    const t = window.localStorage.getItem(TOKEN_KEY);
    if (t) return t;
  } catch { /* ignore */ }
  // Cookie-based auth path: signal "maybe authed" so AuthContext probes /auth/me
  return typeof document !== "undefined" && navigator.cookieEnabled ? "cookie" : null;
}

export function formatError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  if (d && typeof d.msg === "string") return d.msg;
  // Structured payloads (e.g. 409 { error, message, company }) — surface the
  // human-readable message field when present.
  if (d && typeof d.message === "string") return d.message;
  return e?.message || "Something went wrong";
}

/**
 * Dev-only console logger. Silenced in production so we don't leak internal
 * state or pollute the browser console of real users.
 *   logDev.warn("[lms] failed", err);
 *   logDev.error("[auth] denied", err);
 */
const isDev = process.env.NODE_ENV !== "production";
export const logDev = {
  log:   (...a) => { if (isDev) console.log(...a); },
  info:  (...a) => { if (isDev) console.info(...a); },
  warn:  (...a) => { if (isDev) console.warn(...a); },
  error: (...a) => { if (isDev) console.error(...a); },
};
